import React, { useCallback, useState } from "react";

import { useAuth } from "../providers/Auth";
import { CurrentUserSessionIdentity } from "./CurrentUserSessionIdentity";

import { AuthUserFragment } from "@/generated/graphql";

export { resolveDataUser } from "./CurrentUserSessionIdentity";

/**
 * Props for CurrentUserProvider component
 * @property root0.children - Child components that can access current user context
 */
interface Props {
  children: React.ReactNode;
}

/**
 * Result of sign-in confirmation flow (unused in CurrentUser, kept for compatibility)
 * @property success - Whether sign-in confirmation was successful
 * @property session - Session token if confirmation successful
 */
export interface ConfirmSignInResult {
  success: boolean;
  session: string | null | undefined;
}

const CurrentUserContext = React.createContext<
  AuthUserFragment | undefined | null
>(null);

// This hook can be used to access the auth info.
export const useCurrentUser = () => React.useContext(CurrentUserContext);

/** The identity report last received from a session mount. */
interface SessionIdentityReport {
  readonly key: string | undefined;
  readonly user: AuthUserFragment | null | undefined;
}

/**
 * Current user provider component managing authenticated user state.
 *
 * The identity query lives in {@link CurrentUserSessionIdentity}, mounted
 * with `key={sessionKey}` so each sign-in session gets a brand-new AuthMe
 * observable (SE-5482): after sign-out the app clears the Apollo store while
 * the previous observable is active, leaving it hung with the previous
 * user's retained result — remounting per session is the only reliable way
 * to both fetch the new session's identity and make cross-user leakage
 * (SE-5090) structurally impossible. The session key is the refresh token,
 * which is minted once per sign-in and stays stable across access-token
 * refreshes (SE-5105), so mid-session token refreshes never remount.
 *
 * Reports from a previous session's unmounted fetcher are dropped by
 * comparing the echoed session key against the current one.
 *
 * SE-7735: the tri-state below is only meaningful once `AuthProvider` has
 * READ `@authData` back from storage. That read is asynchronous, so on every
 * cold load there is a window in which a signed-in user's `accessToken` is
 * simply not known yet — and reporting `null` there says "signed out", which
 * is a different claim from "not established yet" and is wrong for anyone
 * holding a session. Route gates act on the difference: `squad/_layout.tsx`
 * reads `undefined` as loading and anything else as decisive, so the `null`
 * made a hard load of `/squad` redirect to Home before the session had a
 * chance to appear. The redirect landed, the gate then re-admitted the user
 * one render later, and the Squad screen re-mounted and painted over Home
 * with the router still on `/` — leaving a fully rendered Squad the tab
 * navigator had blurred, so `TabFocusGuard` held it at
 * `web:pointer-events-none` and every click was dead.
 *
 * `auth.isLoaded` is exactly that read's completion signal (it is
 * `data !== undefined` on the provider). The `?? true` keeps the old
 * behaviour when there is no `AuthProvider` above — `useAuth()` returns
 * `null` there and no storage read is ever coming, so waiting on one would
 * strand every consumer on a permanent loading state.
 * @param root0 - Component props
 * @param root0.children - Child components that can access current user context
 */
export function CurrentUserProvider({ children }: Props) {
  const auth = useAuth();
  const isAuthLoaded = auth?.isLoaded ?? true;
  const accessToken = auth?.data?.accessToken ?? undefined;
  const sessionKey = auth?.data?.refreshToken ?? accessToken;

  const [identity, setIdentity] = useState<SessionIdentityReport>({
    key: undefined,
    user: null,
  });

  const handleIdentity = useCallback(
    (key: string | undefined, user: AuthUserFragment | null | undefined) => {
      setIdentity({ key, user });
    },
    []
  );

  // Tri-state contract (consumers depend on it):
  // - null — signed out.
  // - undefined — the session is not established yet: either `@authData` has
  //   not been read back from storage, or it has and this session's identity
  //   has not arrived (which also covers the remount window right after a
  //   different user signs in).
  // - user object — this session's resolved identity.
  const currentSessionUser = !isAuthLoaded
    ? undefined
    : !accessToken
      ? null
      : identity.key === sessionKey
        ? identity.user
        : undefined;

  return (
    <CurrentUserContext.Provider value={currentSessionUser}>
      <CurrentUserSessionIdentity
        key={sessionKey ?? "signed-out"}
        accessToken={accessToken}
        sessionKey={sessionKey}
        onIdentity={handleIdentity}
      />
      {children}
    </CurrentUserContext.Provider>
  );
}
